import { createContext, useContext, useState, useEffect } from "react";
import { licenseFeeService } from "@/services/license-fee-service";
import { LicenseFee, Pagination } from "@/services/license-fee-service/types";
import { ListContextProps } from "./use-list.types";
import { createToast } from "vercel-toast";
import { formatInTimeZone } from "date-fns-tz";
import { isBefore } from "date-fns";
import { UPDATE_PAGINATION } from "@/constants/pagination";

const ListContext = createContext<ListContextProps | undefined>(undefined);

export const ListProvider = ({ children }: { children: React.ReactNode }) => {
  const [data, setData] = useState<LicenseFee[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    take: 10,
  });
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({});

  const updateFilters = (newFilters: Partial<ListContextProps["filters"]>) => {
    if (
      newFilters.startDate &&
      newFilters.endDate &&
      isBefore(
        formatInTimeZone(newFilters.endDate, "UTC", "yyyy-MM-dd"),
        formatInTimeZone(newFilters.startDate, "UTC", "yyyy-MM-dd"),
      )
    ) {
      createToast("Data de início deve ser anterior a data final", {
        type: "error",
        timeout: 2000,
      });

      return;
    }

    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const updateActivePage = (page: number) => {
    setActivePage(page);
  };

  const handleChangePage = async (page: number) => {
    updateActivePage(page);
    fetchData(page);
  };

  const changeActivePage = (update: UPDATE_PAGINATION) => {
    const operation =
      update === UPDATE_PAGINATION.INCREMENT ? activePage + 1 : activePage - 1;
    updateActivePage(operation);
    fetchData(operation);
  };

  const decreasePage = () => {
    changeActivePage(UPDATE_PAGINATION.DECREMENT);
  };

  const increasePage = () => {
    changeActivePage(UPDATE_PAGINATION.INCREMENT);
  };

  const clearFilters = () => {
    setFilters({
      company: "",
      startDate: "",
      endDate: "",
      status: "",
    });
  };

  const fetchData = async (page: number = 1) => {
    setIsLoading(true);
    try {
      const { data, pagination } = await licenseFeeService.listLicenseFees({
        ...filters,
        page: page,
      });

      setData(data);
      setPagination(pagination);

      const countPages = Math.ceil(pagination.total / pagination.take);

      if (countPages === 1 && page > 1) {
        handleChangePage(1);
        setActivePage(1);
      } else {
        setActivePage(page);
      }
      setTotalPages(countPages);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  return (
    <ListContext.Provider
      value={{
        data,
        pagination,
        isLoading,
        filters,
        updateFilters,
        clearFilters,
        activePage,
        handleChangePage,
        decreasePage,
        increasePage,
        totalPages,
      }}
    >
      {children}
    </ListContext.Provider>
  );
};

export const useList = () => {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error("useList must be used within a ListProvider");
  }
  return context;
};
